//  This file provides the legacy `apiFetch` interface for backward compatibility.
//  Internally, all requests are routed through the Axios-based implementation
//  in `services/index.ts` and exports the other services as well through this file.
//  This approach allows existing code to continue working without import changes,
//  along with the Axios's better error handling and interceptors.

import { AxiosError, AxiosRequestConfig } from "axios";
import { AppError, NetworkError } from "./errors";
import {
  api,
  OAuthApi,
  REACT_APP_API_ROOT as API_ROOT,
  getOAuthRoot,
  getOAuthTokenUrl as getOAuthTokenUrlFromService,
  setLogoutHandler as setLogoutHandlerService,
  setRefreshTokenHandler as setRefreshTokenHandlerService,
  apiWithRetry
} from "../services";

export interface FetchOptions extends Omit<RequestInit, "headers"> {
  skipAuth?: boolean;
  headers?: Record<string, string>;
  noResponseParse?: boolean;
  onTokenExpired?: () => void;
}

export const setLogoutHandler = setLogoutHandlerService;
export const setRefreshTokenHandler = setRefreshTokenHandlerService;

export const REACT_APP_API_ROOT = API_ROOT;

/**
 * Builds a single-item detail URL for a Django endpoint.
 *
 * Some endpoints embed a list-only query string in `api_endpoint` — e.g. the
 * synthesised tabular tab uses `api/v2/spatial-datasets/?dataset_type=tabular`.
 * Naively doing `${api_endpoint}${id}/` would yield
 * `.../spatial-datasets/?dataset_type=tabular143/`, which Django parses as the
 * LIST endpoint with a bogus filter (returning `{count:0, results:[]}`) instead
 * of the detail route. Strip any embedded query string so the id always lands on
 * the path. `suffix` (e.g. `?include_relations=true`) is appended after the id.
 */
export const buildItemDetailUrl = (apiEndpoint: string, itemId: string | number, suffix = ""): string => {
  const path = apiEndpoint.split("?")[0];
  return `${API_ROOT}${path}${itemId}/${suffix}`;
};

export const getApiRoot = () => API_ROOT;

export { getOAuthRoot };

export const OAUTH_ROOT = getOAuthRoot();

export const getOAuthTokenUrl = getOAuthTokenUrlFromService;

//Transforms Axios errors to match the original fetch error format
const transformAxiosError = (error: any): Error => {
  // If it's already an AppError, preserve it but inject legacy properties for compatibility
  if (error instanceof AppError) {
    if (error instanceof NetworkError && (error as any).status === undefined) {
      (error as any).status = error.status;
    }
    return error;
  }

  const axiosError = error as AxiosError;
  if (axiosError.response) {
    const status = axiosError.response.status;
    const message =
      (axiosError.response.data as any)?.message || (axiosError.response.data as any)?.detail || `HTTP error! status: ${status}`;

    const transformedError = new Error(message);
    (transformedError as any).status = status;
    (transformedError as any).response = axiosError.response;
    return transformedError;
  }

  if (axiosError.request) {
    return new Error("Network error: No response received");
  }

  return new Error(axiosError.message || "Unknown error occurred");
};

// Maps fetch body to axios data
const transformBody = (body: BodyInit | null | undefined): any => {
  if (!body) return undefined;

  if (body instanceof FormData) {
    return body;
  }

  if (typeof body === "string") {
    try {
      return JSON.parse(body);
    } catch {
      return body;
    }
  }

  return body;
};

//  Internally uses Axios but maintains the original interface for backward compatibility.
export const apiFetch = async (url: string, options: FetchOptions = {}): Promise<any> => {
  // Validate URL
  if (url.includes("undefined")) {
    throw new Error("Malformed URL: URL contains undefined");
  }

  const { method, body, headers, skipAuth, noResponseParse, onTokenExpired, ...restOptions } = options;

  // Build axios config
  const axiosConfig: AxiosRequestConfig & { skipAuth?: boolean; onTokenExpired?: () => void } = {
    url,
    method: (method || "GET").toLowerCase() as any,
    data: transformBody(body),
    headers: headers || {},
    skipAuth,
    onTokenExpired,
    withCredentials: restOptions.credentials === "include"
  };

  try {
    const response = await api.request(axiosConfig);

    // Handle noResponseParse - return response-like object
    if (noResponseParse) {
      return {
        ok: response.status >= 200 && response.status < 300,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        json: async () => response.data,
        text: async () => JSON.stringify(response.data),
        blob: async () => new Blob([JSON.stringify(response.data)])
      };
    }

    return response.data;
  } catch (error) {
    throw transformAxiosError(error as AxiosError);
  }
};

// apiFetchNoResponseRequired - For requests where we don't need the response body
export const apiFetchNoResponseRequired = async (url: string, options: FetchOptions = {}): Promise<boolean> => {
  const { method, body, headers, skipAuth, onTokenExpired } = options;

  const axiosConfig: AxiosRequestConfig & { skipAuth?: boolean; onTokenExpired?: () => void } = {
    url,
    method: (method || "GET").toLowerCase() as any,
    data: transformBody(body),
    headers: headers || {},
    skipAuth,
    onTokenExpired
  };

  try {
    await api.request(axiosConfig);
    return true;
  } catch (error) {
    throw transformAxiosError(error as AxiosError);
  }
};

//  apiFetchWithRetry - Fetch with automatic retry on failure
export const apiFetchWithRetry = async (
  url: string,
  options: FetchOptions = {},
  maxRetries: number = 3,
  delay: number = 1000
): Promise<any> => {
  const { method, body, headers, skipAuth, onTokenExpired } = options;

  return apiWithRetry({
    url,
    method: (method || "GET").toLowerCase() as any,
    data: transformBody(body),
    headers: headers || {},
    skipAuth,
    onTokenExpired,
    maxRetries,
    retryDelay: delay
  } as any);
};

//  fetchOAuthToken - OAuth token request helper
export const fetchOAuthToken = async (formData: FormData) => {
  const tokenUrl = getOAuthTokenUrl();
  try {
    const response = await OAuthApi.post(tokenUrl, formData);
    return response.data;
  } catch (error) {
    throw error;
  }
};
